import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { FolderCode, Layers, Gauge, FileType, ArrowRight } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent = "blue" }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="mt-2 font-heading font-black text-3xl tracking-tight text-slate-900">{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-md flex items-center justify-center bg-${accent}-50 text-${accent}-700`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

const DistributionBar = ({ label, count, total, color = "bg-blue-600" }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-medium text-slate-700">{label}</span>
        <span className="font-mono text-slate-500">{count}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const total = stats?.total || 0;
  const products = stats?.by_product || [];
  const complexities = stats?.by_complexity || [];
  const types = stats?.by_type || [];

  const complexityColor = (name) => {
    if (name === "High") return "bg-red-500";
    if (name === "Medium") return "bg-amber-500";
    return "bg-emerald-500";
  };

  return (
    <div className="p-8 max-w-7xl" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-2">Dashboard</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">
            SQL Query Repository Overview
          </h1>
          <p className="text-sm text-slate-500 mt-1">A consolidated view of your ERP support queries.</p>
        </div>
        <Link
          to="/repository"
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
          data-testid="dashboard-browse-link"
        >
          Browse Repository <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={FolderCode} label="Total Queries" value={total} accent="blue" />
        <StatCard icon={Layers} label="Products Covered" value={products.length} accent="indigo" />
        <StatCard icon={FileType} label="Query Types" value={types.length} accent="violet" />
        <StatCard icon={Gauge} label="High Complexity" value={complexities.find(c => c.name === "High")?.count || 0} accent="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-heading font-bold text-base text-slate-900 mb-4">By Product</h2>
          <div className="space-y-3">
            {products.length === 0 && (
              <div className="text-xs text-slate-400 italic">No data yet</div>
            )}
            {products.map((p) => (
              <DistributionBar key={p.name} label={p.name} count={p.count} total={total} color="bg-blue-600" />
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-heading font-bold text-base text-slate-900 mb-4">By Complexity</h2>
          <div className="space-y-3">
            {complexities.length === 0 && (
              <div className="text-xs text-slate-400 italic">No data yet</div>
            )}
            {complexities.map((c) => (
              <DistributionBar key={c.name} label={c.name} count={c.count} total={total} color={complexityColor(c.name)} />
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h2 className="font-heading font-bold text-base text-slate-900 mb-4">By Type</h2>
          <div className="space-y-3">
            {types.length === 0 && (
              <div className="text-xs text-slate-400 italic">No data yet</div>
            )}
            {types.map((t) => (
              <DistributionBar key={t.name} label={t.name} count={t.count} total={total} color="bg-indigo-600" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
